import { makeAutoObservable, runInAction } from 'mobx';

class GamesStore {
  games = [];
  followingGames = [];
  userGames = []; // Games for viewing other users' profiles
  loading = false;
  error = null;
  rootStore = null;

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // Set loading state
  setLoading(loading) {
    this.loading = loading;
  }

  // Set error state
  setError(error) {
    this.error = error;
  }

  // Fetch user's games
  async fetchGames() {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.rootStore.authenticatedApiCall('api/games?limit=1000');
      
      runInAction(() => {
        // Support both array and paginated object responses
        this.games = Array.isArray(response) ? response : response.items;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      throw error;
    }
  }

  // Fetch following games (friends' games)
  async fetchFollowingGames() {
    this.setLoading(true);
    this.setError(null);

    try {
      const games = await this.rootStore.authenticatedApiCall('api/games/following');
      
      runInAction(() => {
        this.followingGames = games;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      throw error;
    }
  }

  // Fetch games for a specific user by user ID
  async fetchUserGames(userId) {
    this.setLoading(true);
    this.setError(null);
    try {
      const res = await this.rootStore.authenticatedApiCall(`api/games/user/${userId}?limit=1000`, {}, 'GET');
      runInAction(() => {
        // Support both array and paginated object responses
        this.userGames = Array.isArray(res) ? res : res.items;
        this.loading = false;
      });
      return res;
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      throw error;
    }
  }

  // Get user games (for viewing other users' profiles)
  getUserGames() {
    return this.userGames;
  }

  // Clear user games (when leaving profile view)
  clearUserGames() {
    this.userGames = [];
  }

  // Get user season stats (for viewing other users' profiles)
  getUserSeasonStats() {
    const userGames = this.getUserGames();
    
    if (userGames.length === 0) {
      return {
        totalGames: 0,
        wins: 0,
        losses: 0,
        ties: 0,
        totalGoals: 0,
        totalAssists: 0,
        winPercentage: 0,
        // Goalie stats
        totalSaves: 0,
        totalShotsAgainst: 0,
        totalGoalsAgainst: 0,
        savePercentage: 0,
        goalsAgainstAverage: 0,
        shutouts: 0
      };
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const seasonStart = new Date(currentYear, 8, 1); // September 1st
    const seasonEnd = new Date(currentYear + 1, 7, 31); // August 31st

    const currentSeasonGames = userGames.filter(game => {
      const gameDate = new Date(game.date);
      return gameDate >= seasonStart && gameDate < seasonEnd;
    });

    return this.calculateSeasonStats(currentSeasonGames);
  }

  // Get user last season stats (for viewing other users' profiles)
  getUserLastSeasonStats() {
    const userGames = this.getUserGames();
    
    if (userGames.length === 0) {
      return {
        totalGames: 0,
        wins: 0,
        losses: 0,
        ties: 0,
        totalGoals: 0,
        totalAssists: 0,
        winPercentage: 0,
        // Goalie stats
        totalSaves: 0,
        totalShotsAgainst: 0,
        totalGoalsAgainst: 0,
        savePercentage: 0,
        goalsAgainstAverage: 0,
        shutouts: 0
      };
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const lastSeasonStart = new Date(currentYear - 1, 8, 1); // September 1st of last year
    const lastSeasonEnd = new Date(currentYear, 7, 31); // August 31st of this year

    const lastSeasonGames = userGames.filter(game => {
      const gameDate = new Date(game.date);
      return gameDate >= lastSeasonStart && gameDate < lastSeasonEnd;
    });

    return this.calculateSeasonStats(lastSeasonGames);
  }

  // Helper method to calculate season stats
  calculateSeasonStats(games) {
    if (games.length === 0) {
      return {
        totalGames: 0,
        wins: 0,
        losses: 0,
        ties: 0,
        totalGoals: 0,
        totalAssists: 0,
        winPercentage: 0,
        // Goalie stats
        totalSaves: 0,
        totalShotsAgainst: 0,
        totalGoalsAgainst: 0,
        savePercentage: 0,
        goalsAgainstAverage: 0,
        shutouts: 0
      };
    }

    const stats = games.reduce((acc, game) => {
      acc.totalGames++;
      acc.totalGoals += game.goals || 0;
      acc.totalAssists += game.assists || 0;

      // Goalie stats
      acc.totalSaves += game.saves || 0;
      acc.totalShotsAgainst += game.shots_against || 0;
      acc.totalGoalsAgainst += game.goals_against || 0;
      
      // Check for shutout (goals against = 0)
      if (game.goals_against === 0) {
        acc.shutouts++;
      }

      if (game.our_score > game.their_score) {
        acc.wins++;
      } else if (game.our_score < game.their_score) {
        acc.losses++;
      } else {
        acc.ties++;
      }

      return acc;
    }, {
      totalGames: 0,
      wins: 0,
      losses: 0,
      ties: 0,
      totalGoals: 0,
      totalAssists: 0,
      // Goalie stats
      totalSaves: 0,
      totalShotsAgainst: 0,
      totalGoalsAgainst: 0,
      shutouts: 0
    });

    stats.winPercentage = stats.totalGames > 0 
      ? ((stats.wins / stats.totalGames) * 100).toFixed(1)
      : 0;

    // Calculate goalie percentages
    stats.savePercentage = stats.totalShotsAgainst > 0 
      ? ((stats.totalSaves / stats.totalShotsAgainst) * 100).toFixed(1)
      : 0;
    
    // Calculate GAA (assuming 60 minutes per game average)
    const totalMinutes = games.reduce((acc, game) => acc + (game.minutes_played || 60), 0);
    stats.goalsAgainstAverage = totalMinutes > 0 
      ? ((stats.totalGoalsAgainst / totalMinutes) * 60).toFixed(2)
      : 0;

    return stats;
  }

  // Create a new game
  async createGame(gameData) {
    this.setLoading(true);
    this.setError(null);

    try {
      const newGame = await this.rootStore.authenticatedApiCall(
        'api/games',
        {},
        'POST',
        gameData
      );
      
      runInAction(() => {
        this.games.unshift(newGame); // Add to beginning of array
        this.loading = false;
      });

      return newGame;
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      throw error;
    }
  }

  // Update a game
  async updateGame(gameId, gameData) {
    this.setLoading(true);
    this.setError(null);

    try {
      const updatedGame = await this.rootStore.authenticatedApiCall(
        `api/games/${gameId}`,
        {},
        'PUT',
        gameData
      );

      runInAction(() => {
        const index = this.games.findIndex(game => game.id === gameId);
        if (index !== -1) {
          this.games[index] = updatedGame;
        }
        this.loading = false;
      });

      return { success: true, game: updatedGame };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Delete a game
  async deleteGame(gameId) {
    this.setLoading(true);
    this.setError(null);

    try {
      await this.rootStore.authenticatedApiCall(
        `api/games/${gameId}`,
        {},
        'DELETE'
      );

      runInAction(() => {
        this.games = this.games.filter(game => game.id !== gameId);
        this.loading = false;
      });

      return { success: true };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Get current season dates (August 1 to August 1)
  getCurrentSeasonDates() {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed
    
    let seasonStart, seasonEnd;
    
    if (currentMonth >= 7) { // August or later (month 7+)
      // Current season started this year on August 1
      seasonStart = new Date(currentYear, 7, 1); // August 1
      seasonEnd = new Date(currentYear + 1, 7, 1); // Next year August 1
    } else {
      // Current season started last year on August 1
      seasonStart = new Date(currentYear - 1, 7, 1); // Last year August 1
      seasonEnd = new Date(currentYear, 7, 1); // This year August 1
    }
    
    return { seasonStart, seasonEnd };
  }

  // Get current season games
  getCurrentSeasonGames() {
    const { seasonStart, seasonEnd } = this.getCurrentSeasonDates();
    
    return this.games.filter(game => {
      const gameDate = new Date(game.date);
      return gameDate >= seasonStart && gameDate < seasonEnd;
    });
  }

  // Get season stats for current season only
  getSeasonStats() {
    const currentSeasonGames = this.getCurrentSeasonGames();
    
    if (currentSeasonGames.length === 0) {
      return {
        totalGames: 0,
        wins: 0,
        losses: 0,
        ties: 0,
        totalGoals: 0,
        totalAssists: 0,
        winPercentage: 0,
        // Goalie stats
        totalSaves: 0,
        totalShotsAgainst: 0,
        totalGoalsAgainst: 0,
        savePercentage: 0,
        goalsAgainstAverage: 0,
        shutouts: 0
      };
    }

    const stats = currentSeasonGames.reduce((acc, game) => {
      acc.totalGames++;
      acc.totalGoals += game.goals || 0;
      acc.totalAssists += game.assists || 0;

      // Goalie stats
      acc.totalSaves += game.saves || 0;
      acc.totalShotsAgainst += game.shots_against || 0;
      acc.totalGoalsAgainst += game.goals_against || 0;
      
      // Check for shutout (goals against = 0)
      if (game.goals_against === 0) {
        acc.shutouts++;
      }

      if (game.our_score > game.their_score) {
        acc.wins++;
      } else if (game.our_score < game.their_score) {
        acc.losses++;
      } else {
        acc.ties++;
      }

      return acc;
    }, {
      totalGames: 0,
      wins: 0,
      losses: 0,
      ties: 0,
      totalGoals: 0,
      totalAssists: 0,
      // Goalie stats
      totalSaves: 0,
      totalShotsAgainst: 0,
      totalGoalsAgainst: 0,
      shutouts: 0
    });

    stats.winPercentage = stats.totalGames > 0 
      ? ((stats.wins / stats.totalGames) * 100).toFixed(1)
      : 0;

    // Calculate goalie percentages
    stats.savePercentage = stats.totalShotsAgainst > 0 
      ? ((stats.totalSaves / stats.totalShotsAgainst) * 100).toFixed(1)
      : 0;
    
    // Calculate GAA (assuming 60 minutes per game average)
    const totalMinutes = currentSeasonGames.reduce((acc, game) => acc + (game.minutes_played || 60), 0);
    stats.goalsAgainstAverage = totalMinutes > 0 
      ? ((stats.totalGoalsAgainst / totalMinutes) * 60).toFixed(2)
      : 0;

    return stats;
  }

  // Get last season stats
  getLastSeasonStats() {
    const { seasonStart } = this.getCurrentSeasonDates();
    
    // Calculate last season dates
    const lastSeasonStart = new Date(seasonStart.getFullYear() - 1, 7, 1);
    const lastSeasonEnd = new Date(seasonStart);
    
    const lastSeasonGames = this.games.filter(game => {
      const gameDate = new Date(game.date);
      return gameDate >= lastSeasonStart && gameDate < lastSeasonEnd;
    });
    
    if (lastSeasonGames.length === 0) {
      return {
        totalGames: 0,
        wins: 0,
        losses: 0,
        ties: 0,
        totalGoals: 0,
        totalAssists: 0,
        winPercentage: 0,
        // Goalie stats
        totalSaves: 0,
        totalShotsAgainst: 0,
        totalGoalsAgainst: 0,
        savePercentage: 0,
        goalsAgainstAverage: 0,
        shutouts: 0
      };
    }

    const stats = lastSeasonGames.reduce((acc, game) => {
      acc.totalGames++;
      acc.totalGoals += game.goals || 0;
      acc.totalAssists += game.assists || 0;

      // Goalie stats
      acc.totalSaves += game.saves || 0;
      acc.totalShotsAgainst += game.shots_against || 0;
      acc.totalGoalsAgainst += game.goals_against || 0;
      
      // Check for shutout (goals against = 0)
      if (game.goals_against === 0) {
        acc.shutouts++;
      }

      if (game.our_score > game.their_score) {
        acc.wins++;
      } else if (game.our_score < game.their_score) {
        acc.losses++;
      } else {
        acc.ties++;
      }

      return acc;
    }, {
      totalGames: 0,
      wins: 0,
      losses: 0,
      ties: 0,
      totalGoals: 0,
      totalAssists: 0,
      // Goalie stats
      totalSaves: 0,
      totalShotsAgainst: 0,
      totalGoalsAgainst: 0,
      shutouts: 0
    });

    stats.winPercentage = stats.totalGames > 0 
      ? ((stats.wins / stats.totalGames) * 100).toFixed(1)
      : 0;

    // Calculate goalie percentages
    stats.savePercentage = stats.totalShotsAgainst > 0 
      ? ((stats.totalSaves / stats.totalShotsAgainst) * 100).toFixed(1)
      : 0;
    
    // Calculate GAA (assuming 60 minutes per game average)
    const totalMinutes = lastSeasonGames.reduce((acc, game) => acc + (game.minutes_played || 60), 0);
    stats.goalsAgainstAverage = totalMinutes > 0 
      ? ((stats.totalGoalsAgainst / totalMinutes) * 60).toFixed(2)
      : 0;

    return stats;
  }

  // Get recent games
  getRecentGames(limit = 5) {
    return this.games.slice(0, limit);
  }

  // Get games by date range
  getGamesByDateRange(startDate, endDate) {
    return this.games.filter(game => {
      const gameDate = new Date(game.date);
      return gameDate >= startDate && gameDate <= endDate;
    });
  }

  // Clear error
  clearError() {
    this.error = null;
  }

  // Reset store
  reset() {
    this.games = [];
    this.loading = false;
    this.error = null;
  }
}

export { GamesStore }; 